import qs from 'query-string'

import { USER_SERVICE_BASE_URL } from '../../api/config'
import axios from '../../function/axiosInstance'
import extract from '../../function/extract'

type UpdateInviteReturnType = Promise<{
  status: 'error' | 'ok'
  data: null | { records: unknown; status: 'success' }
  error: string | undefined
}>

interface UpdateInviteArgs {
  companyId: string
  memberId?: string
  inviteId?: string
  role?: TeamMemberRole
  cancelInvite: boolean
}

export const updateInvite = async (
  args: UpdateInviteArgs
): UpdateInviteReturnType => {
  try {
    const { companyId, memberId, inviteId, role, cancelInvite = false } = args

    const params = qs.stringify(
      { cancel_invite: cancelInvite, member_id: memberId, invite_id: inviteId },
      { skipEmptyString: true, skipNull: true }
    )

    const url = `/companies/${companyId}/team-members?${params}`

    const response = await axios.put(url, { role })

    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

type DeleteMemberReturnType = Promise<{
  status: 'error' | 'ok'
  data: null | { records: unknown; status: 'success' }
  error: string | undefined
}>

interface DeleteMemberArgs {
  companyId: string
  memberId: string
}

export const deleteMember = async (
  args: DeleteMemberArgs
): DeleteMemberReturnType => {
  try {
    const { companyId, memberId } = args

    const endpoint = `${USER_SERVICE_BASE_URL}/companies/${companyId}/team-members/${memberId}`

    const response = await axios.delete(endpoint)

    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

type GetPreferencesReturnType = Promise<{
  status: 'error' | 'ok'
  data: {
    records: PreferenceRecord[]
    status: string
  }
  error: string | undefined
}>

export const getPreferences = async (): GetPreferencesReturnType => {
  try {
    const response = await axios.get(`/companies/preferences`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const leaveOrganisation = async (
  companyId: string
): Promise<ApiResponse> => {
  try {
    const response = await axios.delete(
      `${USER_SERVICE_BASE_URL}/companies/${companyId}/leave`
    )
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const updateCountry = async (
  companyId: string,
  countryCode: string
): Promise<ApiResponse> => {
  try {
    const response = await axios.put(`/companies/${companyId}`, {
      country_code: countryCode
    })
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const updateCompanyData = async (
  companyId: string,
  body: unknown
): Promise<ApiResponse> => {
  try {
    const response = await axios.put(`/companies/${companyId}`, body)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getCompanies = async (
  queryParams: Record<string, string | number>
): Promise<ApiResponse> => {
  try {
    const stringifiedQueryParams = qs.stringify(queryParams, {
      skipEmptyString: true,
      skipNull: true
    })
    const response = await axios.get(`/companies?${stringifiedQueryParams}`)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getRecentVerticals = async (
  companyId: string
): Promise<ApiResponseV2<{ response: VerticalID[] }>> => {
  try {
    const response = await axios.get(`/companies/${companyId}/recent_verticals`)
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}
